/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, Save, RotateCcw } from 'lucide-react';

const BAND_TONES = {
  IDEAL:     'bg-emerald-100 text-emerald-800 border-emerald-300',
  STRONG:    'bg-sky-100 text-sky-800 border-sky-300',
  NURTURE:   'bg-amber-100 text-amber-800 border-amber-300',
  HIGH_RISK: 'bg-rose-100 text-rose-800 border-rose-300',
};

/**
 * RSS scoring panel — manual entry for 9 dimensions, live composite preview,
 * band display, history (latest only in v1), admin override action.
 */
export default function RssScoreCard({ leadId, isAdmin, role }) {
  const [dims, setDims] = useState([]);
  const [bands, setBands] = useState([]);
  const [score, setScore] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideForm, setOverrideForm] = useState({ composite_final: '', reason: '' });
  const canScore = role !== 'representative';

  const reload = useCallback(async () => {
    try {
      const [dResp, sResp] = await Promise.all([
        axios.get(`${API}/terra-growth/rss/dimensions`),
        axios.get(`${API}/terra-growth/rss/${leadId}`).catch(() => ({ data: { exists: false } })),
      ]);
      setDims(dResp.data.dimensions);
      setBands(dResp.data.bands);
      if (sResp.data?.exists && sResp.data?.score) {
        setScore(sResp.data.score);
        setForm({ ...sResp.data.score.raw });
      } else {
        setScore(null);
        setForm({});
      }
    } catch (e) {
      toast.error('Could not load RSS data.');
    }
  }, [leadId]);

  useEffect(() => { reload(); }, [reload]);

  const livePreview = (() => {
    let total = 0;
    let s = 0;
    dims.forEach(d => {
      const v = parseFloat(form[d.code]);
      if (Number.isFinite(v)) {
        total += v * d.weight;
        s += d.weight;
      }
    });
    if (s === 0) return null;
    const composite = Math.round((total / s) * 10) / 10 * (s); // simple weighted
    const score10 = Math.round(total * 10) / 10;
    const band = [...bands].sort((a, b) => b.min - a.min).find(b => score10 >= b.min);
    return { score: score10, band };
  })();

  const save = async () => {
    if (!canScore) return;
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      await axios.post(`${API}/terra-growth/rss/${leadId}`, payload);
      toast.success('RSS scored.');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save RSS.');
    } finally {
      setBusy(false);
    }
  };

  const submitOverride = async () => {
    if (!isAdmin) return;
    if (!overrideForm.reason || overrideForm.reason.length < 3) {
      toast.error('Override reason is required.');
      return;
    }
    const v = parseFloat(overrideForm.composite_final);
    if (!Number.isFinite(v) || v < 0 || v > 100) {
      toast.error('Composite must be between 0 and 100.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/terra-growth/rss/${leadId}/override`, {
        composite_final: v, reason: overrideForm.reason,
      });
      toast.success('RSS override applied.');
      setOverrideOpen(false);
      setOverrideForm({ composite_final: '', reason: '' });
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed.');
    } finally {
      setBusy(false);
    }
  };

  const composite = score?.composite_final ?? livePreview?.score ?? null;
  const band = score?.band || livePreview?.band || null;

  return (
    <div data-testid="rss-score-card" className="rounded-2xl border border-[#EBE8E3] bg-white p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h3 className="text-base font-['Outfit'] font-light text-[#2C2A29]">Right Subscriber Score</h3>
          <p className="text-[11px] text-[#8A8681] font-['Work_Sans'] mt-0.5">
            Score 0–100 per dimension. Weighted composite shown live.
          </p>
        </div>
        {composite !== null && (
          <div className="flex items-center gap-2">
            <span data-testid="rss-composite" className="text-3xl font-['Outfit'] font-light text-[#2C2A29] tabular-nums">
              {composite.toFixed(1)}
            </span>
            {band && (
              <span data-testid={`rss-band-${band.code}`}
                    className={`text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-full border ${BAND_TONES[band.code] || ''}`}>
                {band.label}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {dims.map(d => (
          <div key={d.code} data-testid={`rss-dim-${d.code}`} className="flex flex-col gap-1">
            <label className="text-[11px] font-['Work_Sans'] text-[#5C5955] flex justify-between">
              <span>{d.label}</span>
              <span className="text-[#B5B0A8] tabular-nums">w {Math.round(d.weight * 100)}%</span>
            </label>
            <Input
              type="number" min="0" max="100"
              value={form[d.code] ?? ''}
              onChange={(e) => setForm(f => ({ ...f, [d.code]: e.target.value }))}
              disabled={!canScore || busy}
              placeholder="0–100"
              data-testid={`rss-dim-input-${d.code}`}
              className="h-9 text-sm"
            />
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-5">
        <div className="text-[11px] text-[#8A8681] font-['Work_Sans']">
          {score?.is_overridden && (
            <span data-testid="rss-overridden-flag" className="inline-flex items-center gap-1 text-[#7A3E3E]">
              <RotateCcw className="w-3 h-3" /> Admin override · {score.override_reason || 'reason on record'}
            </span>
          )}
          {!canScore && <span>Representatives can view but not set the final score.</span>}
        </div>
        <div className="flex gap-2">
          {canScore && (
            <Button
              data-testid="rss-save-btn" onClick={save} disabled={busy}
              className="bg-[#4A5D4E] hover:bg-[#3D4F40] text-white text-xs rounded-full px-4"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          )}
          {isAdmin && (
            <Button
              data-testid="rss-override-toggle-btn"
              onClick={() => setOverrideOpen(v => !v)} variant="outline"
              className="border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-4"
            >
              <RotateCcw className="w-3 h-3" /> Override
            </Button>
          )}
        </div>
      </div>

      {isAdmin && overrideOpen && (
        <div data-testid="rss-override-form" className="mt-4 p-3 rounded-xl border border-rose-200 bg-rose-50/40 space-y-2">
          <p className="text-[11px] uppercase tracking-[0.16em] text-rose-800">Admin override — final composite</p>
          <Input
            type="number" min="0" max="100" placeholder="Final composite (0–100)"
            value={overrideForm.composite_final}
            onChange={(e) => setOverrideForm(f => ({ ...f, composite_final: e.target.value }))}
            data-testid="rss-override-composite-input" className="h-9"
          />
          <Input
            placeholder="Reason — required"
            value={overrideForm.reason}
            onChange={(e) => setOverrideForm(f => ({ ...f, reason: e.target.value }))}
            data-testid="rss-override-reason-input" className="h-9"
          />
          <Button
            onClick={submitOverride} disabled={busy}
            data-testid="rss-override-submit-btn"
            className="bg-rose-700 hover:bg-rose-800 text-white text-xs rounded-full px-4"
          >
            Apply override
          </Button>
        </div>
      )}
    </div>
  );
}
